// src/components/themes/default/store/branch/BranchHeader.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Store,
  Search,
  ShoppingBag,
  User,
  LogOut,
  LayoutDashboard,
  ChevronDown,
} from "lucide-react";
import { useBranchCart } from "./BranchCartContext";

interface BranchHeaderProps {
  branch?: {
    id?: number;
    name?: string;
    slug?: string;
    contact_phone?: string;
    address?: string;
  } | null;
  primaryColor?: string;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  onOpenAuth?: () => void;
  branchUser?: any;
  onLogout?: () => void;
  onOpenCustomerDashboard?: () => void;
}

export default function BranchHeader({
  branch,
  primaryColor = "#059669",
  searchQuery = "",
  setSearchQuery = () => {},
  onOpenAuth = () => {},
  branchUser = null,
  onLogout = () => {},
  onOpenCustomerDashboard = () => {},
}: BranchHeaderProps) {
  const { totalItemCount, setIsCartOpen } = useBranchCart();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const safeBranch = branch || {
    id: 0,
    name: "Branch Outlet",
    slug: "",
    contact_phone: "",
    address: "",
  };

  const branchSlug = safeBranch.slug || "";
  const branchName = safeBranch.name || "Branch Outlet";

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/80 px-4 py-3 sm:py-3.5 shadow-xs transition-all">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
        
        {/* Branch Identity & Logo */}
        <Link
          href={branchSlug ? `/b/${branchSlug}` : "#"}
          className="flex items-center gap-2.5 min-w-0"
        >
          <div
            style={{ backgroundColor: primaryColor }}
            className="w-9 h-9 rounded-2xl flex items-center justify-center text-white shadow-md shadow-emerald-600/20 shrink-0"
          >
            <Store className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 block leading-none">
              Verified Branch Outlet
            </span>
            <h1 className="text-base sm:text-lg font-black text-slate-900 truncate">
              {branchName}
            </h1>
          </div>
        </Link>

        {/* Search */}
        <div className="hidden md:flex flex-1 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search products in ${branchName}...`}
            className="w-full pl-10 pr-4 py-2 rounded-2xl text-xs font-medium bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white transition-all placeholder:text-slate-400"
          />
        </div>

        {/* Auth & Cart */}
        <div className="flex items-center gap-2 shrink-0">
          {branchUser ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-emerald-50/70 border border-emerald-200 text-xs font-bold text-emerald-900 hover:bg-emerald-100 transition-all cursor-pointer active:scale-95"
              >
                <div
                  style={{ backgroundColor: primaryColor }}
                  className="w-6 h-6 rounded-full text-white text-[11px] font-black flex items-center justify-center shadow-xs"
                >
                  {(branchUser.first_name || branchUser.email || "C").charAt(0).toUpperCase()}
                </div>
                <span className="truncate max-w-[85px] sm:max-w-[110px]">
                  {branchUser.first_name || (branchUser.email ? branchUser.email.split("@")[0] : "Customer")}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl border border-slate-200 shadow-2xl p-2 z-50 animate-fadeIn space-y-1">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block">
                      Outlet Member
                    </span>
                    <p className="text-xs font-bold text-slate-900 truncate">
                      {branchUser.first_name || "Customer"}
                    </p>
                    <p className="text-[10px] font-mono text-slate-400 truncate">{branchUser.email}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onOpenCustomerDashboard();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors cursor-pointer"
                  >
                    <LayoutDashboard className="w-4 h-4 text-emerald-600" />
                    <span>My Dashboard</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onLogout();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 text-rose-500" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={onOpenAuth}
              className="flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-bold border border-slate-200 hover:bg-slate-50 text-slate-700 transition-all cursor-pointer active:scale-95"
            >
              <User className="w-3.5 h-3.5 text-emerald-600" />
              <span>Sign In / Register</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
            className="relative flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-2xl font-bold text-xs hover:opacity-90 transition-all cursor-pointer border border-emerald-500/20 active:scale-95"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden xs:inline">Branch Cart</span>
            {totalItemCount > 0 && (
              <span
                style={{ backgroundColor: primaryColor }}
                className="w-5 h-5 rounded-full text-white text-[10px] font-black flex items-center justify-center shadow-xs"
              >
                {totalItemCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}